import { Link, graphql, useStaticQuery } from 'gatsby';
import React from 'react';
import {
  FacebookIcon,
  InstagramIcon,
  LinkedinIcon,
  TiktokIcon,
  TwitterIcon,
  YoutubeIcon
} from '../../assets/icons/SocialIcons';
import AccordionBlock from './accordionBlock';
import { siteLogoLink } from './utils/Logo';

/**
 * Renders out the footer.
 * @return {JSX}
 */
export default function Footer() {
  const year = new Date().getFullYear();
  let internalSites: any = [];
  let usefulLinks: any = [];

  if (process.env.GATSBY_INTERNAL_SITES) {
    try {
      internalSites = JSON.parse(process.env.GATSBY_INTERNAL_SITES as any);
    } catch (err) {
      console.error('error formating internal sites', process.env.GATSBY_INTERNAL_SITES);
    }
  }

  if (process.env.GATSBY_USEFUL_LINKS) {
    try {
      usefulLinks = JSON.parse(process.env.GATSBY_USEFUL_LINKS as any);
    } catch (err) {
      console.error('error formating useful links', process.env.GATSBY_USEFUL_LINKS);
    }
  }

  const data = useStaticQuery(graphql`
    query {
      site {
        siteMetadata {
          title
          brandName
          logo
          buildVersion
          socialPlatforms {
            facebook
            linkedin
            twitter
            youtube
            instagram
            tiktok
          }
        }
      }
    }
  `);

  const GTiSitesContent = (
    <ul className="space-y-5 leading-4 pt-5">
      {internalSites.map((item: any, index: number) => {
        return (
          <li key={`footer_internal_link_${index}`}>
            <a
              href={item.href}
              target="_blank"
              rel="noreferrer"
              className="font-medium hover:text-primary-1100 text-xs text-gray line-clamp-1"
              aria-label={item.label}
            >
              {item.label}
            </a>
          </li>
        );
      })}
    </ul>
  );

  const footerNavs = [
    {
      title: 'Legal',
      content: (
        <ul className="space-y-5 leading-4 pt-5">
          <li>
            <a
              href="https://groupgti.com/privacy/gtimedia"
              className="font-medium hover:text-primary-1100 text-xs text-gray"
              aria-label="Privacy policy"
              target="_blank"
              rel="noreferrer"
            >
              Privacy policy
            </a>
          </li>
          <li>
            <a
              href="https://groupgti.com/terms/advertisers"
              className="font-medium hover:text-primary-1100 text-xs text-gray"
              aria-label="Terms and Conditions for employers"
              target="_blank"
              rel="noreferrer"
            >
              Terms and conditions for employers
            </a>
          </li>
          <li>
            <a
              href="https://groupgti.com/terms/students"
              className="font-medium hover:text-primary-1100 text-xs text-gray"
              aria-label="Terms and Conditions for students"
              target="_blank"
              rel="noreferrer"
            >
              Terms and conditions for students
            </a>
          </li>
          <li>
            <a
              href="https://groupgti.com/about-our-cookies"
              className="font-medium hover:text-primary-1100 text-xs text-gray"
              aria-label="About our cookies"
              target="_blank"
              rel="noreferrer"
            >
              About our cookies
            </a>
          </li>
          <li>
            <button
              className="btn-link font-medium hover:text-primary-1100 text-xs text-gray text-left"
              onClick={() => {
                // eslint-disable-next-line no-undef
                cookiefirst_show_settings();
              }}
            >
              Manage cookie settings
            </button>
          </li>
          <li>
            <a
              href="https://groupgti.com/accessibility"
              className="font-medium hover:text-primary-1100 text-xs text-gray"
              aria-label="Accessibility"
              target="_blank"
              rel="noreferrer"
            >
              Accessibility
            </a>
          </li>
        </ul>
      )
    },
    {
      title: 'GTI Sites',
      content: GTiSitesContent
    },
    {
      title: 'Useful Links',
      content: (
        <ul className="space-y-5 leading-4 pt-5">
          {usefulLinks.map((item: any, index: number) => {
            if (item.to) {
              return (
                <li key={`footer_useful_link_${index}`}>
                  <Link
                    to={item.to}
                    className="font-medium hover:text-primary-1100 text-xs text-gray"
                    aria-label={item.label}
                  >
                    {item.label}
                  </Link>
                </li>
              );
            } else {
              return (
                <li key={`footer_useful_link_${index}`}>
                  <a
                    href={item.href}
                    target="_blank"
                    className="font-medium hover:text-primary-1100 text-xs text-gray"
                    aria-label={item.label}
                    rel="noreferrer"
                  >
                    {item.label}
                  </a>
                </li>
              );
            }
          })}
        </ul>
      )
    },
    {
      title: 'Follow Us',
      content: (
        <ul className="mt-4 leading-4 pt-5 md:mt-0 space-x-7 md:space-y-5 md:space-x-0">
          {data.site.siteMetadata.socialPlatforms.facebook && (
            <li className="inline-block md:block">
              <a
                href={data.site.siteMetadata.socialPlatforms.facebook}
                target="_blank"
                aria-label="Facebook"
                rel="noreferrer"
              >
                <FacebookIcon className="-mt-0.5 h-6 md:h-3 inline-flex w-auto" />
                <span className="hidden md:inline-block text-xs ml-4 font-medium">Facebook</span>
              </a>
            </li>
          )}
          {data.site.siteMetadata.socialPlatforms.linkedin && (
            <li className="inline-block md:block">
              <a
                href={data.site.siteMetadata.socialPlatforms.linkedin}
                target="_blank"
                aria-label="LinkedIn"
                rel="noreferrer"
              >
                <LinkedinIcon className="-mt-0.5 h-6 md:h-3 inline-flex w-auto" />
                <span className="hidden md:inline-block text-xs ml-4 font-medium">LinkedIn</span>
              </a>
            </li>
          )}
          {data.site.siteMetadata.socialPlatforms.twitter && (
            <li className="inline-block md:block">
              <a
                href={data.site.siteMetadata.socialPlatforms.twitter}
                target="_blank"
                aria-label="Twitter"
                rel="noreferrer"
              >
                <TwitterIcon className="-mt-0.5 h-6 md:h-3 inline-flex w-auto" fill="#17191C" />
                <span className="hidden md:inline-block text-xs ml-4 font-medium">Twitter</span>
              </a>
            </li>
          )}
          {data.site.siteMetadata.socialPlatforms.youtube && (
            <li className="inline-block md:block">
              <a
                href={data.site.siteMetadata.socialPlatforms.youtube}
                target="_blank"
                aria-label="YouTube"
                rel="noreferrer"
              >
                <YoutubeIcon className="-mt-0.5 h-8 md:h-4 inline-flex w-auto" />
                <span className="hidden md:inline-block text-xs ml-4 font-medium">YouTube</span>
              </a>
            </li>
          )}
          {data.site.siteMetadata.socialPlatforms.instagram && (
            <li className="inline-block md:block">
              <a
                href={data.site.siteMetadata.socialPlatforms.instagram}
                target="_blank"
                aria-label="Instagram"
                rel="noreferrer"
              >
                <InstagramIcon className="-mt-0.5 h-8 md:h-4 inline-flex w-auto" />
                <span className="hidden md:inline-block text-xs ml-4 font-medium">Instagram</span>
              </a>
            </li>
          )}
          {data.site.siteMetadata.socialPlatforms.tiktok && (
            <li className="inline-block md:block">
              <a
                href={data.site.siteMetadata.socialPlatforms.tiktok}
                target="_blank"
                aria-label="TikTok"
                rel="noreferrer"
              >
                <TiktokIcon className="-mt-0.5 h-6 md:h-3 inline-flex w-auto" />
                <span className="hidden md:inline-block text-xs ml-4 font-medium">TikTok</span>
              </a>
            </li>
          )}
        </ul>
      )
    }
  ];

  return (
    <footer className="section-container-full border-t border-gray-200">
      <div className="section-container-2xl pb-5 md:pb-10">
        <footer className="container max-w-6xl pt-8 md:pt-12">
          <div className="md:flex lg:max-w-6xl border-gray-200 border-b">
            <div className="md:w-28 mb-7 md:mb-0 md:mt-1">{siteLogoLink(data, '', 'h-8', true)}</div>
            <div className="border-t border-grey-200 md:border-none divide-solid divide-y md:divide-y-0 md:gap-12 md:grid md:grid-cols-4 pb-4 md:pb-0">
              {footerNavs.map((footerNav: any, index: number) => {
                return (
                  <React.Fragment key={`footer-${index}`}>
                    <div className="md:hidden">
                      <AccordionBlock
                        content={[
                          {
                            question: footerNav.title,
                            answer: footerNav.content
                          }
                        ]}
                        indent={false}
                        containerClasses="my-4 !px-0"
                        accordionItemClasses="border-none py-0 px-0 shadow-none rounded-none bg-white"
                        questionClasses="text-sm uppercase tracking-widest text-gray hover:text-primary-1100 font-bold md:font-medium"
                      />
                    </div>
                    <div className="hidden md:block md:pb-8">
                      <h4 className="uppercase text-gray text-sm font-bold w-full flex justify-between items-start tracking-widest md:border-0 md:after:block md:after:empty md:after:h-0.5 md:after:w-7 md:after:bg-current md:block cursor-default">
                        <span className="mb-1.5 block">{footerNav.title}</span>
                      </h4>
                      <div>{footerNav.content}</div>
                    </div>
                  </React.Fragment>
                );
              })}
            </div>
          </div>
          <div className="lg:flex lg:justify-between lg:max-w-6xl md:mt-5">
            <div className="mt-5 md:mt-0">
              <div className="text-xs text-gray-700">
                &copy; Copyright {year} GTI Futures Ltd. Registered in England No. 2347472.
                <br />
                The Fountain Building, Howbery Park, Benson Lane, Wallingford, Oxfordshire OX10 8BA UK.
              </div>
            </div>
            <div className="block mt-5 md:mt-0">
              <svg
                className="w-20 dark-host:fill-white"
                aria-labelledby="builtbygroupgti"
                width="140"
                height="45"
                viewBox="0 0 140 45"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M10.224 22.384C10.832 22.48 11.384 22.728 11.88 23.128C12.392 23.528 12.792 24.024 13.08 24.616C13.384 25.208 13.536 25.84 13.536 26.512C13.536 27.36 13.32 28.128 12.888 28.816C12.456 29.488 11.824 30.024 10.992 30.424C10.176 30.808 9.208 31 8.088 31H1.848V14.272H7.848C8.984 14.272 9.952 14.464 10.752 14.848C11.552 15.216 12.152 15.72 12.552 16.36C12.952 17 13.152 17.72 13.152 18.52C13.152 19.512 12.88 20.336 12.336 20.992C11.808 21.632 11.104 22.096 10.224 22.384ZM4.032 21.496H7.704C8.728 21.496 9.52 21.256 10.08 20.776C10.64 20.296 10.92 19.632 10.92 18.784C10.92 17.936 10.64 17.272 10.08 16.792C9.52 16.312 8.712 16.072 7.656 16.072H4.032V21.496ZM7.896 29.2C8.984 29.2 9.832 28.944 10.44 28.432C11.048 27.92 11.352 27.208 11.352 26.296C11.352 25.368 11.032 24.64 10.392 24.112C9.752 23.568 8.896 23.296 7.824 23.296H4.032V29.2H7.896ZM28.2308 17.848V31H26.0468V29.056C25.6308 29.728 25.0468 30.256 24.2948 30.64C23.5588 31.008 22.7428 31.192 21.8468 31.192C20.8228 31.192 19.9028 30.984 19.0868 30.568C18.2708 30.136 17.6228 29.496 17.1428 28.648C16.6788 27.8 16.4468 26.768 16.4468 25.552V17.848H18.6068V25.264C18.6068 26.56 18.9348 27.56 19.5908 28.264C20.2468 28.952 21.1428 29.296 22.2788 29.296C23.4468 29.296 24.3668 28.936 25.0388 28.216C25.7108 27.496 26.0468 26.448 26.0468 25.072V17.848H28.2308ZM33.0463 15.712C32.6303 15.712 32.2783 15.568 31.9903 15.28C31.7023 14.992 31.5583 14.64 31.5583 14.224C31.5583 13.808 31.7023 13.456 31.9903 13.168C32.2783 12.88 32.6303 12.736 33.0463 12.736C33.4463 12.736 33.7823 12.88 34.0543 13.168C34.3423 13.456 34.4863 13.808 34.4863 14.224C34.4863 14.64 34.3423 14.992 34.0543 15.28C33.7823 15.568 33.4463 15.712 33.0463 15.712ZM34.1023 17.848V31H31.9183V17.848H34.1023ZM40.0086 13.24V31H37.8246V13.24H40.0086ZM46.3948 19.648V27.4C46.3948 28.04 46.5308 28.496 46.8028 28.768C47.0748 29.024 47.5468 29.152 48.2188 29.152H49.8268V31H47.8588C46.6428 31 45.7308 30.72 45.1228 30.16C44.5148 29.6 44.2108 28.68 44.2108 27.4V19.648H42.5068V17.848H44.2108V14.536H46.3948V17.848H49.8268V19.648H46.3948ZM61.0554 20.296C61.5034 19.512 62.1594 18.872 63.0234 18.376C63.8874 17.88 64.8714 17.632 65.9754 17.632C67.1594 17.632 68.2234 17.912 69.1674 18.472C70.1114 19.032 70.8554 19.824 71.3994 20.848C71.9434 21.856 72.2154 23.032 72.2154 24.376C72.2154 25.704 71.9434 26.888 71.3994 27.928C70.8554 28.968 70.1034 29.776 69.1434 30.352C68.1994 30.928 67.1434 31.216 65.9754 31.216C64.8394 31.216 63.8394 30.968 62.9754 30.472C62.1274 29.976 61.4874 29.344 61.0554 28.576V31H58.8714V13.24H61.0554V20.296ZM69.9834 24.376C69.9834 23.384 69.7834 22.52 69.3834 21.784C68.9834 21.048 68.4394 20.488 67.7514 20.104C67.0794 19.72 66.3354 19.528 65.5194 19.528C64.7194 19.528 63.9754 19.728 63.2874 20.128C62.6154 20.512 62.0714 21.08 61.6554 21.832C61.2554 22.568 61.0554 23.424 61.0554 24.4C61.0554 25.392 61.2554 26.264 61.6554 27.016C62.0714 27.752 62.6154 28.32 63.2874 28.72C63.9754 29.104 64.7194 29.296 65.5194 29.296C66.3354 29.296 67.0794 29.104 67.7514 28.72C68.4394 28.32 68.9834 27.752 69.3834 27.016C69.7834 26.264 69.9834 25.384 69.9834 24.376ZM86.4182 17.848L78.4982 37.192H76.2422L78.8342 30.856L73.5302 17.848H75.9542L80.0822 28.504L84.1622 17.848H86.4182Z"
                  fill="black"
                ></path>
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M95 4.57219C95 2.04704 97.047 0 99.5722 0H135.428C137.953 0 140 2.04704 140 4.57219V40.4278C140 42.953 137.953 45 135.428 45H99.5722C97.047 45 95 42.953 95 40.4278V4.57219ZM109.714 26.9094C110.805 26.9465 111.878 26.5386 112.655 25.7597C113.414 24.9808 113.839 23.9052 113.784 22.8111C113.821 21.717 113.414 20.6414 112.655 19.8625C111.878 19.0836 110.805 18.6571 109.695 18.6942C108.604 18.6571 107.549 19.0651 106.772 19.844C106.014 20.6414 105.607 21.6984 105.644 22.7925C105.607 23.9052 106.014 24.9623 106.772 25.7597C107.549 26.5386 108.622 26.9651 109.714 26.9094ZM113.895 16.6172H119.648V12.4261L122.386 12.0923L122.589 12.2592V16.6172H126.714V18.9724H122.589V25.871C122.589 27.4102 123.237 28.189 124.532 28.189C125.105 28.2076 125.66 27.9665 126.067 27.5585L126.326 27.6698L127.195 29.5242L127.121 29.7839C126.289 30.47 125.253 30.8224 124.18 30.8224C122.959 30.878 121.794 30.4329 120.906 29.5799C120.018 28.6341 119.574 27.3545 119.648 26.0749V18.9909H116.429V28.3374C116.54 30.1918 115.818 31.9907 114.468 33.2517C113.044 34.3829 111.249 34.9763 109.436 34.8836C108.178 34.9022 106.92 34.6425 105.755 34.1233C104.7 33.6597 103.757 32.9364 103.036 32.0278V31.7681L104.497 30.2289H104.719C105.81 31.731 107.568 32.5841 109.418 32.5099C110.509 32.5656 111.582 32.2132 112.433 31.5271C113.247 30.8038 113.691 29.7468 113.617 28.6526V27.6698C112.433 28.7639 110.861 29.3573 109.233 29.3017C105.718 29.3944 102.795 26.6313 102.701 23.1078V22.7369C102.664 20.9937 103.33 19.3062 104.569 18.0822C105.827 16.8397 107.529 16.1536 109.286 16.2092C111.007 16.1907 112.653 16.8768 113.856 18.1193V16.6172H113.893H113.895ZM129.081 30.4515H132.023V16.6172H129.081V30.4515ZM129.118 13.6501C129.488 14.0581 130.006 14.2806 130.543 14.2806C131.098 14.2991 131.634 14.0952 132.023 13.6872C132.411 13.2792 132.633 12.7414 132.615 12.1851C132.633 11.6287 132.411 11.0909 132.004 10.72C131.634 10.3306 131.098 10.1081 130.561 10.1081C130.025 10.0895 129.488 10.3121 129.118 10.7015C128.73 11.0909 128.508 11.6102 128.526 12.1665C128.508 12.7229 128.73 13.2606 129.118 13.6501Z"
                  fill="#17191C"
                ></path>
              </svg>
              <p className="md:text-right text-xs pt-1">{'v' + data.site.siteMetadata.buildVersion}</p>
            </div>
          </div>
        </footer>
      </div>
    </footer>
  );
}
